import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '../../components/base/SeoHead';

interface Product {
  id: number;
  name: string;
  price: number;
  image: string;
  category: string;
  description: string;
  badge?: string;
}

export default function ShopPage() {
  const [selectedCategory, setSelectedCategory] = useState('全部');
  const [sortBy, setSortBy] = useState('featured');
  const [priceRange, setPriceRange] = useState([0, 20000]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const products: Product[] = [
    {
      id: 1,
      name: 'MOFU 智能寵物空氣清淨機',
      price: 9900,
      image: 'https://static.readdy.ai/image/1951b0480a7991f717d8723701e95243/7d4217cd548672bb2ebe9dfb77a9d576.jpeg',
      category: '空氣清淨機',
      description: '專為寵物家庭設計，有效去除異味與過敏原',
      badge: '熱銷'
    }
  ];

  const categories = ['全部', '空氣清淨機', '香氛產品', '寵物用品', '配件'];

  const filteredProducts = products.filter(product => {
    if (selectedCategory !== '全部' && product.category !== selectedCategory) {
      return false;
    }
    if (product.price < priceRange[0] || product.price > priceRange[1]) {
      return false;
    }
    return true;
  });

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (sortBy === 'price-low') return a.price - b.price;
    if (sortBy === 'price-high') return b.price - a.price;
    return 0;
  });

  const addToCart = (product: Product) => {
    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    const existingItem = cart.find((item: any) => item.id === product.id);

    if (existingItem) {
      existingItem.quantity += 1;
    } else {
      cart.push({ ...product, quantity: 1 });
    }

    localStorage.setItem('cart', JSON.stringify(cart));
    window.dispatchEvent(new Event('cartUpdated'));

    // Show success message
    const message = document.createElement('div');
    message.className = 'fixed top-24 right-6 bg-coral-500 text-white px-6 py-3 rounded-full shadow-lg z-50 animate-fade-in';
    message.textContent = '已加入購物車';
    document.body.appendChild(message);
    setTimeout(() => message.remove(), 2000);
  };

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://mofu.com.tw';

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${siteUrl}/shop#webpage`,
        "url": `${siteUrl}/shop`,
        "name": "線上商城 - 寵物空氣清淨機與香氛產品 | MOFU 淨寵居",
        "description": "探索 MOFU 淨寵居的寵物空氣清淨機、寵物友善香氛產品。提供空氣清淨機、擴香組、香氛蠟燭等多種寵物安全產品，全台免運，7天鑑賞期。",
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "首頁", "item": siteUrl },
            { "@type": "ListItem", "position": 2, "name": "線上商城", "item": `${siteUrl}/shop` }
          ]
        }
      },
      {
        "@type": "ItemList",
        "name": "MOFU 淨寵居商品列表",
        "description": "寵物空氣清淨機與寵物友善香氛產品",
        "numberOfItems": products.length,
        "itemListElement": products.slice(0, 10).map((product, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "item": {
            "@type": "Product",
            "name": product.name,
            "description": product.description,
            "image": product.image,
            "brand": { "@type": "Brand", "name": "MOFU 淨寵居" },
            "offers": {
              "@type": "Offer",
              "price": product.price,
              "priceCurrency": "TWD",
              "availability": "https://schema.org/InStock",
              "url": `${siteUrl}/product/${product.id}`
            }
          }
        }))
      }
    ]
  };

  // 判断是否显示"未来会持续上市新品"提示
  const showComingSoon = ['香氛產品', '寵物用品', '配件'].includes(selectedCategory) && sortedProducts.length === 0;

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <SeoHead
        title="線上商城 - 新北市新莊區寵物空氣清淨機與香氛產品 | MOFU 淨寵居"
        description="探索 MOFU 淨寵居的寵物空氣清淨機、寵物友善香氛產品。提供空氣清淨機、擴香組、香氛蠟燭等多種寵物安全產品，全台免運，7天鑑賞期。寵物空氣清淨機、寵物友善香氛是我們的核心商品。"
        keywords="寵物空氣清淨機,寵物友善香氛,香氛產品,線上商城,新北市新莊區"
        ogImage={products[0]?.image}
        schema={schema}
      />

      {/* Header */}
      <div className="bg-white py-16 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-800 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            線上商城
          </h1>
          <p className="text-gray-600 text-lg">探索我們的寵物空氣清淨機與香氛產品</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Filters */}
          <div className="lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-3xl p-6 sticky top-24">
              <h3 className="text-lg font-bold text-gray-800 mb-4">商品分類</h3>
              <div className="space-y-2">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`w-full text-left px-4 py-3 rounded-xl transition-all cursor-pointer whitespace-nowrap ${
                      selectedCategory === category
                        ? 'bg-coral-500 text-white'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>

              <div className="mt-8">
                <h3 className="text-lg font-bold text-gray-800 mb-4">價格範圍</h3>
                <div className="space-y-4">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>NT$ {priceRange[0]}</span>
                    <span>NT$ {priceRange[1]}</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="20000"
                    step="100"
                    value={priceRange[1]}
                    onChange={(e) => setPriceRange([0, parseInt(e.target.value)])}
                    className="w-full"
                  />
                </div>
              </div>

              <div className="mt-8">
                <h3 className="text-lg font-bold text-gray-800 mb-4">排序方式</h3>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-coral-500 transition-colors text-sm cursor-pointer"
                >
                  <option value="featured">精選推薦</option>
                  <option value="price-low">價格：低到高</option>
                  <option value="price-high">價格：高到低</option>
                </select>
              </div>
            </div>
          </div>

          {/* Products Grid */}
          <div className="flex-1">
            <div className="mb-6 flex justify-between items-center">
              <p className="text-gray-600">顯示 {sortedProducts.length} 件商品</p>
            </div>

            {showComingSoon ? (
              <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                  <div className="w-24 h-24 flex items-center justify-center bg-coral-100 rounded-full mx-auto mb-6">
                    <i className="ri-time-line text-5xl text-coral-500"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-3">未來會持續上市新品</h3>
                  <p className="text-gray-600 mb-8">敬請期待更多精彩產品</p>
                  <Link
                    to="/shop"
                    onClick={() => setSelectedCategory('全部')}
                    className="inline-block px-8 py-3 bg-coral-500 text-white rounded-full font-medium hover:bg-coral-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    查看全部商品
                  </Link>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop>
                {sortedProducts.map((product) => (
                  <div
                    key={product.id}
                    className="bg-white rounded-3xl overflow-hidden hover:shadow-xl hover:-translate-y-2 transition-all duration-300 group"
                  >
                    <Link to={`/product/${product.id}`} className="block relative cursor-pointer">
                      <div className="relative aspect-square overflow-hidden bg-gray-50">
                        <div className="w-full h-full">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        {product.badge && (
                          <div className="absolute top-4 right-4 px-3 py-1 bg-coral-500 text-white rounded-full text-xs font-medium whitespace-nowrap">
                            {product.badge}
                          </div>
                        )}
                      </div>
                    </Link>

                    <div className="p-6">
                      <span className="text-xs text-gray-500 mb-2 block">{product.category}</span>
                      <Link to={`/product/${product.id}`} className="cursor-pointer">
                        <h3 className="text-lg font-bold text-gray-800 mb-2 line-clamp-2 group-hover:text-coral-500 transition-colors">
                          {product.name}
                        </h3>
                      </Link>
                      <p className="text-sm text-gray-600 mb-4 line-clamp-1">{product.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-2xl font-bold text-coral-500">
                          NT$ {product.price.toLocaleString()}
                        </span>
                        <button
                          onClick={() => addToCart(product)}
                          className="px-4 py-2 bg-coral-500 text-white rounded-full text-sm font-medium hover:bg-coral-600 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          加入購物車
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}